export type AskOutputFormat = "string" | "number" | "date" | "money";

export type AskFormulaInput = {
  question: string;
  outputFormat: AskOutputFormat;
  contextParts: string[];
};

export type AskFormulaParseResult =
  | { ok: true; value: AskFormulaInput }
  | { ok: false; message: string };

const OUTPUT_FORMATS = new Set<AskOutputFormat>(["string", "number", "date", "money"]);

export function parseAskFormulaArguments(rawUserArgs: unknown[]): AskFormulaParseResult {
  const [questionArg, ...optionalArgs] = rawUserArgs;
  const questionPartsResult = flattenArgumentValues(questionArg);
  if (!questionPartsResult.ok) return questionPartsResult;

  const question = joinParts(questionPartsResult.value);
  if (!question) {
    return { ok: false, message: "Invalid input provided." };
  }

  let outputFormat: AskOutputFormat | null = null;
  const contextParts: string[] = [];

  for (const group of expandOptionalArgumentGroups(optionalArgs)) {
    const partsResult = flattenArgumentValues(group);
    if (!partsResult.ok) return partsResult;

    const parts = partsResult.value;
    if (parts.length === 0) continue;

    const format = parts.length === 1 ? parseOutputFormat(parts[0]) : null;
    if (format && !outputFormat) {
      outputFormat = format;
      continue;
    }

    contextParts.push(...parts);
  }

  return {
    ok: true,
    value: {
      question,
      outputFormat: outputFormat ?? "string",
      contextParts,
    },
  };
}

/** True for the words that set the output format: string, number, date, money. */
export function isOutputFormat(value: string): boolean {
  return parseOutputFormat(value) !== null;
}

/** The text of one formula argument (a value or a range), or null when it is empty. */
export function formulaArgumentText(value: unknown): string | null {
  const result = flattenArgumentValues(value);
  if (!result.ok) return null;
  const text = joinParts(result.value);
  return text || null;
}

function expandOptionalArgumentGroups(args: unknown[]): unknown[] {
  const groups: unknown[] = [];

  for (const arg of args) {
    if (isRepeatingMatrixArgument(arg)) {
      groups.push(...arg);
    } else {
      groups.push(arg);
    }
  }

  return groups;
}

function isRepeatingMatrixArgument(value: unknown): value is unknown[][][] {
  return Array.isArray(value) && value.length > 0 && value.every(isMatrixArgument);
}

function isMatrixArgument(value: unknown): value is unknown[][] {
  return Array.isArray(value) && value.every((row) => Array.isArray(row));
}

function flattenArgumentValues(value: unknown): { ok: true; value: string[] } | { ok: false; message: string } {
  const parts: string[] = [];
  const error = collectArgumentValues(value, parts);
  if (error) return { ok: false, message: error };
  return { ok: true, value: parts };
}

function collectArgumentValues(value: unknown, parts: string[]): string | null {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const error = collectArgumentValues(item, parts);
      if (error) return error;
    }
    return null;
  }

  if (typeof value === "string") {
    const text = normalizeCellText(value);
    if (text) parts.push(text);
    return null;
  }

  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    parts.push(String(value));
    return null;
  }

  return "Invalid context provided.";
}

function parseOutputFormat(value: string): AskOutputFormat | null {
  const normalized = value.trim().toLowerCase();
  return OUTPUT_FORMATS.has(normalized as AskOutputFormat) ? (normalized as AskOutputFormat) : null;
}

function normalizeCellText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function joinParts(parts: string[]): string {
  return parts.map(normalizeCellText).filter(Boolean).join(" ").trim();
}
